/**
 * The bag, everywhere. One controller owns add, quantity, remove and
 * discount for the drawer, the cart page and the product page, because all
 * four do the same two things: post to the Ajax cart, then swap in whatever
 * Liquid rendered back.
 *
 * Nothing here is required for the shop to work. Every control it binds is a
 * real link, a real form or a real submit button first, so with this file
 * missing the bag still fills, empties and reaches checkout on full page
 * loads.
 */
class MilkCart extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-drawer]');
    if (!this.dialog) return;

    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.closing = null;

    document.documentElement.setAttribute('data-cart-enhanced', '');

    /* Everything is delegated from the document. The drawer and the cart page
       render the same controls from the same snippets, and only one of the two
       is ever inside this element, so binding here is what keeps a plus button
       behaving identically wherever it was rendered. */
    document.addEventListener('click', this.onClick);
    document.addEventListener('change', this.onChange);
    document.addEventListener('submit', this.onSubmit);

    this.dialog.addEventListener('cancel', this.onCancel);
    this.dialog.addEventListener('click', this.onBackdropClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onClick);
    document.removeEventListener('change', this.onChange);
    document.removeEventListener('submit', this.onSubmit);
  }

  /* ---------------------------------------------------------------- routes */

  get root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /**
   * The drawer and the header count are always refreshed. The cart page adds
   * its own section id, which it publishes on itself rather than having this
   * file guess what the template called it.
   */
  get sections() {
    const list = this.dataset.sections.split(',');
    const page = document.querySelector('[data-cart-section]');
    if (page) list.push(page.dataset.cartSection);
    return list.join(',');
  }

  async post(path, body) {
    const response = await fetch(this.root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: this.sections, sections_url: location.pathname }),
    });
    const data = await response.json();
    if (!response.ok) throw data;
    return data;
  }

  /* ---------------------------------------------------------------- render */

  /**
   * Swaps the parts that changed and leaves the dialog itself alone. The
   * dialog holds the open state and the focus trap, so replacing it would
   * shut the drawer every time someone pressed plus.
   */
  apply(data, settleKey) {
    const sections = data.sections || {};

    Object.keys(sections).forEach((id) => {
      const html = sections[id];
      if (!html) return;

      const fresh = new DOMParser().parseFromString(html, 'text/html');

      this.swap(fresh, '[data-drawer-body]');
      this.swap(fresh, '[data-cart-page-body]');
      this.swap(fresh, '[data-cart-count]');
      this.swap(fresh, '[data-drawer-head-count]');
    });

    if (settleKey) {
      const line = this.querySelector(`[data-cart-key="${settleKey}"]`);
      if (line && !this.reduced.matches) line.setAttribute('data-settled', '');
    }
  }

  swap(fresh, selector) {
    const next = fresh.querySelector(selector);
    const current = document.querySelector(selector);
    if (next && current) current.replaceWith(next);
  }

  /* ------------------------------------------------------------------ open */

  open() {
    if (this.dialog.open) return;
    clearTimeout(this.closing);

    const gutter = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--milk-scroll-gutter', `${gutter}px`);
    document.documentElement.classList.add('milk-cart-open');

    this.dialog.showModal();

    const panel = this.querySelector('[data-drawer-panel]');
    if (panel) panel.focus({ preventScroll: true });

    requestAnimationFrame(() => this.dialog.setAttribute('data-open', ''));
  }

  close() {
    if (!this.dialog.open) return;
    this.dialog.removeAttribute('data-open');

    const finish = () => {
      this.dialog.close();
      document.documentElement.classList.remove('milk-cart-open');
    };

    if (this.reduced.matches) finish();
    else this.closing = setTimeout(finish, 380);
  }

  onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /* A click that lands on the dialog itself missed the panel, so it is the
     backdrop. showModal's own backdrop is not an element we can listen to. */
  onBackdropClick = (event) => {
    if (event.target === this.dialog) this.close();
  };

  /* ------------------------------------------------------------- delegation */

  onClick = (event) => {
    if (this.modified(event)) return;

    if (event.target.closest('[data-cart-toggle]')) {
      event.preventDefault();
      this.open();
      return;
    }

    if (event.target.closest('[data-drawer-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    const step = event.target.closest('[data-cart-step]');
    if (step) {
      event.preventDefault();
      const field = step.parentElement.querySelector('[data-cart-qty]');
      const next = Number(field.value) + Number(step.dataset.cartStep);
      this.changeLine(step, Math.max(0, next));
      return;
    }

    if (event.target.closest('[data-cart-remove]')) {
      event.preventDefault();
      this.changeLine(event.target.closest('[data-cart-remove]'), 0);
    }
  };

  onChange = (event) => {
    if (!event.target.matches('[data-cart-qty]')) return;
    this.changeLine(event.target, Math.max(0, Number(event.target.value)));
  };

  /**
   * Product forms are matched on their action rather than on a hook we had to
   * add, so a form rendered by an app is picked up on the same terms as ours.
   */
  onSubmit = (event) => {
    const action = event.target.getAttribute('action') || '';

    if (action.indexOf('/cart/add') !== -1) {
      event.preventDefault();
      this.addToCart(event.target);
      return;
    }

    this.cartFormSubmit(event);
  };

  cartFormSubmit(event) {
    if (event.target.matches('[data-discount-form]')) {
      event.preventDefault();
      this.applyDiscount(event.target);
      return;
    }

    if (event.target.matches('[data-discount-clear]')) {
      event.preventDefault();
      this.setDiscount('', event.target);
    }
  }

  modified(event) {
    return (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    );
  }

  /* ------------------------------------------------------------------ verbs */

  async changeLine(origin, quantity) {
    const row = origin.closest('[data-cart-line]');
    if (!row) return;

    row.setAttribute('data-busy', '');

    try {
      const data = await this.post('cart/change.js', {
        line: Number(row.dataset.cartLine),
        quantity,
      });
      this.apply(data);
    } catch (error) {
      row.removeAttribute('data-busy');
      this.report(row, error);
    }
  }

  /**
   * The product form posts itself rather than a hand-built payload, so every
   * field the merchant or an app added — line item properties, selling
   * plans, quantity — travels with it untouched.
   */
  async addToCart(form) {
    const button = form.querySelector('[data-add-button]');
    const fields = new FormData(form);
    const item = {
      id: fields.get('id'),
      quantity: Number(fields.get('quantity') || 1),
    };

    if (fields.get('selling_plan')) item.selling_plan = fields.get('selling_plan');

    /* Line item properties travel through untouched, so an engraving field
       or an app's own input reaches the cart the way it was typed. */
    const properties = {};
    fields.forEach((value, key) => {
      if (key.indexOf('properties[') === 0) properties[key.slice(11, -1)] = value;
    });
    if (Object.keys(properties).length) item.properties = properties;

    this.clearError(form);
    this.state(button, 'busy');

    try {
      const data = await this.post('cart/add.js', { items: [item] });
      const added = (data.items && data.items[0]) || data;

      this.apply(data, added && added.key);
      this.state(button, 'done');
      this.announce(form, button);
      this.open();
    } catch (error) {
      this.state(button, 'idle');
      this.report(form, error);
    }
  }

  /* The tick is drawn for the eye only. This is the same news, spoken. */
  announce(form, button) {
    const slot = form.querySelector('[data-add-status]');
    const done = button && button.querySelector('.milk-add__done');
    if (slot && done) slot.textContent = done.textContent.trim();
  }

  async applyDiscount(form) {
    const input = form.querySelector('[data-discount-input]');
    const code = input.value.trim();

    if (!code) {
      this.discountError(form, form.dataset.missingMessage);
      input.focus();
      return;
    }

    await this.setDiscount(code, form, input);
  }

  /**
   * Shopify reports whether a code can be redeemed, so a wrong one is caught
   * here and never stored. What it cannot do is take the money off before
   * checkout, which is why the code is kept rather than the saving shown.
   */
  async setDiscount(code, form, input) {
    const button = form.querySelector('button[type="submit"]');
    if (button) button.setAttribute('data-busy', '');

    try {
      const data = await this.post('cart/update.js', {
        discount: code,
        attributes: { 'Discount code': code },
      });

      const match = (data.discount_codes || []).find(
        (entry) => entry.code.toLowerCase() === code.toLowerCase()
      );

      if (code && match && match.applicable === false) {
        await this.post('cart/update.js', { discount: '', attributes: { 'Discount code': '' } });
        this.discountError(form, form.dataset.invalidMessage);
        if (input) input.select();
        return;
      }

      this.apply(data);
    } catch (error) {
      this.report(form, error);
    } finally {
      if (button) button.removeAttribute('data-busy');
    }
  }

  discountError(form, message) {
    const slot = form.parentElement.querySelector('[data-discount-error]');
    if (!slot) return;
    slot.textContent = message;
    slot.hidden = false;
  }

  clearError(scope) {
    scope.querySelectorAll('[data-cart-error], [data-discount-error]').forEach((slot) => {
      slot.hidden = true;
      slot.textContent = '';
    });
  }

  /* ----------------------------------------------------------------- states */

  state(button, name) {
    if (!button) return;
    clearTimeout(button.milkTimer);

    if (name === 'idle') {
      button.removeAttribute('data-state');
      button.disabled = false;
      return;
    }

    button.setAttribute('data-state', name);
    button.disabled = name === 'busy';

    if (name === 'done') {
      button.milkTimer = setTimeout(() => {
        button.removeAttribute('data-state');
        button.disabled = false;
      }, 2000);
    }
  }

  /**
   * Shopify's own words where it gave us any — "You can only add 3 to your
   * cart" beats anything this file could invent. The generic line is the
   * fallback for a request that never reached Shopify at all.
   */
  report(scope, error) {
    const message = (error && error.description) || this.dataset.errorMessage;
    const slot =
      scope.querySelector('[data-cart-error]') || document.querySelector('[data-cart-error]');
    if (!slot || !message) return;
    slot.textContent = message;
    slot.hidden = false;
  }
}

if (!customElements.get('milk-cart')) {
  customElements.define('milk-cart', MilkCart);
}
